<template>
    <div class="flex md:hidden">
        <div class="flex flex-col w-full">
            <div>
                <div class="bg-porcelain2 w-full py-6 lg-down:pl-5 pr-5 lg-2xl:pl-5 pl-40">
                    <div class="flex flex-col">
                        <div class="mb-4">
                            <h2 class="text-3xl font-bold text-shark leading-none mt-2 mb-1">{{'Find a Dealer'|t}}</h2>
                            <span class="text-shuttle text-sm">{{'Find the Allura dealer nearest you'|t}}</span>
                        </div>
                        <div class="mb-4">
                            <text-input :label="'Enter search location'|t" :placeholder="'Zip code'|t"
                                        v-model="searchLocationLocal"
                                        @input="() => $emit('update:searchLocation', searchLocationLocal)"/>
                        </div>
                        <div class="mb-4">
                          <div class="flex flex-wrap items-center gap-1 lg:gap-2">
                            <div v-for="category in availableCategories" :key="category.slug" class="first:ml-0">
                              <label
                                  :for="category.slug"
                                  class="block select-none px-2 py-1 leading-none font-bold text-center text-xxs transition-colors duration-200 ease-in-out border"
                                  :class="{
                                          'bg-lochmara text-white border-transparent font-semibold': searchCategory === category.title,
                                          'border-gray-600 text-gray-600 font-normal hover:bg-gray-100 cursor-pointer': searchCategory !== category.title
                                        }"
                              >
                                <input
                                    type="radio"
                                    name="category-picker"
                                    :id="category.slug"
                                    :value="category.title"
                                    class="peer hidden"
                                    v-model="searchCategoryLocal"
                                    @change="() => $emit('update:searchCategory', searchCategoryLocal)"/>
                                {{ category.title }}
                              </label>
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div class="relative">
                    
                    <div class="bg-gradient-b-black-transparent absolute h-4 w-full opacity-15"></div>

                    <GmapMap
                            ref="map"
                            :center="{lat:44.063710, lng:-91.939550}"
                            :zoom="zoom"
                            map-type-id="terrain"
                            style="width: 100%; height: 350px"
                    >
                        <gmap-custom-marker
                                v-for="dealer in dealers"
                                :key="dealer.uid"
                                :clickable="true"
                                :marker="{lat: dealer.dealerAddress.lat, lng: dealer.dealerAddress.lng}">
                            <img @click="() => handleDealerPinClicked(dealer)" :src="getIcon(dealer)" />
                        </gmap-custom-marker>
                    </GmapMap>
                    
                </div>
            </div>
            <div v-if="loading">
                <div class="text-xs text-shuttle py-3 border-iron2 border-b pr-5 lg-down:pl-5 lg-2xl:pl-5 pl-40">
                    {{'Loading...'|t}}
                </div>
            </div>
            <div class="text-xs text-shuttle py-3 border-iron2 border-b pr-5 lg-down:pl-5 lg-2xl:pl-5 pl-40" v-else>
                {{'Enter your zip code above to get started.'|t}}
            </div>
            <div>
                <ul class="flex flex-col">
                    <dealer-item-mobile v-for="dealer in dealers"
                                         :key="dealer.uid"
                                         :dealer-address="dealer.dealerAddress"
                                         :email-address="dealer.emailAddress"
                                         :phone-number="dealer.phoneNumber"
                                         :backerboard-only="dealer.backerboardOnly"
                                         :categories="dealer.categories"
                                         :directions="dealer.directions"
                                         :uid="dealer.uid"
                                         :title="dealer.title"
                                         v-on:expanded="handleDealerExpanded"
                    />
                </ul>
            </div>
            <div v-if="loading === false && pageInfo.total_pages > 1 && pageInfo.current_page !== pageInfo.total_pages">
                <button v-if="!loadingMore" v-on:click="$emit('loadMore')" type="button"
                        class="bg-porcelain2 py-5 text-center w-full flex items-center justify-center text-xs text-shuttle transition hover:bg-iron2">
                    <span class="pr-2">{{'Click for more search results'|t}}</span>
                    <svg class="fill-current" width="12" height="7" xmlns="http://www.w3.org/2000/svg">
                        <path class="fill-current transition"
                              d="M6 5.137L10.731.226c.2903-.3013.761-.3013 1.0513 0 .2903.3014.2903.79 0 1.0913L6.5257 6.774c-.2904.3013-.761.3013-1.0514 0L.2177 1.3173c-.2903-.3013-.2903-.79 0-1.0913.2903-.3013.761-.3013 1.0514 0L6 5.137z"
                              fill-rule="evenodd"/>
                    </svg>
                </button>
                <div class="bg-porcelain2 py-5 text-center w-full flex items-center justify-center text-xs text-shuttle transition hover:bg-iron2"
                     v-else-if="loadingMore === true">{{'Loading...'|t}}
                </div>
            </div>
        </div>
    </div>
</template>



<script>
    import DealerItemMobile from "./DealerItemMobile.vue";
    import TextInput from "../AlluraUI/TextInput.vue";
    import CustomDropdown from "../AlluraUI/CustomDropdown.vue";
    import GmapCustomMarker from 'vue2-gmap-custom-marker';

    import activePin from '../../img/icons-svg/map-pin-active.svg?url';
    import inactivePin from '../../img/icons-svg/map-pin-inactive.svg?url';
    import inactivePinTagged from '../../img/icons-svg/map-pin-tagged-inactive.svg?url';

    export default {
        props: {
            dealers: Array,
            searchLocation: String,
            searchCategory: String,
            availableCategories: Array,
            onSearchChange: Function,
            pageInfo: Object,
            loading: Boolean,
            loadingMore: Boolean
        },
        components: {
            CustomDropdown,
            TextInput,
            DealerItemMobile,
            'gmap-custom-marker': GmapCustomMarker
        },
        watch: {
            dealers: function () {
                const bounds = new google.maps.LatLngBounds();
                for (let dealer of this.dealers) {
                    bounds.extend({lat: dealer.dealerAddress.lat, lng: dealer.dealerAddress.lng});
                }
                this.$refs.map.$mapObject.fitBounds(bounds);
            }
        },
        data() {
            return {
                searchLocationLocal: this.searchLocation,
                searchCategoryLocal: this.searchCategory,
                zoom: 7,
                inactivePinUrl: window.inactivePinUrl,
                inactivePinUrlTagged: window.inactivePinUrlTagged,
                activePinUrl: window.activePinUrl,
                activeDealerUid: null
            }
        },
        name: 'DealerListMobile',
        methods: {
            handleDealerPinClicked(dealer) {
                this.$events.$emit('pinclicked', dealer);
                this.activeDealerUid = dealer.uid;
            },
            handleDealerExpanded(data) {
                this.activeDealerUid = data.uid;
                this.$refs.map.panTo({
                    lat: data.dealerAddress.lat,
                    lng: data.dealerAddress.lng
                });
                this.zoom = 17;
            },
            getIcon(dealer) {
              if (dealer.uid === this.activeDealerUid) {
                return activePin;
              }
              if (dealer.backerboardOnly) {
                return inactivePinTagged;
              }
              return inactivePin;
            }
        }
    }
</script>